// iter-35g Phase 2 — Dynamic Bump renderer.
// Walks the configured bumps for the current viewport's left + right sides
// and renders any whose source_type points at a creator-private source
// (Workbench slots, File Refinery slots, staging buffers, result buffers).
//
// Legacy bumps (bookmarks/tools/discover/invite) are still rendered by
// SideBumps.jsx and DiscoverBumps.jsx — this component is additive.
//
// Owner-only: dynamic bumps render only when:
//   • the silo prop is provided (i.e. we're on a creator-side page) AND
//   • the current user owns the silo OR is super admin.
// Otherwise the dynamic bumps are silently filtered out so fans visiting
// /s/{slug} don't see private staging content.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowsClockwise, ArrowSquareOut, Pin, PushPin, PushPinSlash } from "@phosphor-icons/react";
import * as PIcons from "@phosphor-icons/react";
import { api, getUser } from "@/lib/api";
import { useBumpConfig, slotToTopPct, COLOR_SLOT_VAR, SHAPE_CLASS, SHAPE_CLASS_RIGHT } from "@/lib/bumpConfig";
import { useTabsOff, registerHiddenTabs, useOpenTabListener } from "@/lib/tabToggle";
import ModelOutputControl from "@/components/ModelOutputControl";

// Dynamic source_type prefixes that this component handles.
const DYNAMIC_PREFIXES = ["workbench-", "file-refinery-"];

// localStorage key for the pinned-drawer state. At most 1 left + 1 right.
const PIN_KEY = "mysilo_dynbump_pinned_v1";

function readPins() {
  try { return JSON.parse(localStorage.getItem(PIN_KEY)) || {}; } catch { return {}; }
}
function writePins(p) {
  try { localStorage.setItem(PIN_KEY, JSON.stringify(p)); } catch { /* quota */ }
}

const SOURCE_TO_SURFACE = {
  // Maps drawer source_type → the LLM-routing surface id (iter-30) so the
  // staple module shows the right model + Boost behavior for that work.
  "workbench-slot-1": "workbench_run",
  "workbench-slot-2": "workbench_run",
  "workbench-slot-3": "workbench_run",
  "workbench-stage": "workbench_run",
  "workbench-result": "workbench_run",
  "file-refinery-slot-1": "refinery",
  "file-refinery-slot-2": "refinery",
  "file-refinery-slot-3": "refinery",
  "file-refinery-stage": "refinery",
  "file-refinery-result": "refinery",
};

function isDynamic(sourceType) {
  return DYNAMIC_PREFIXES.some((p) => (sourceType || "").startsWith(p));
}

export default function DynamicBumps({ silo }) {
  const { cfg, viewport } = useBumpConfig();
  const me = getUser();
  const isOwner = me && silo && (me.id === silo.owner_user_id || me.role === "super_admin");
  const [pins, setPins] = useState(readPins());

  // Persist pin changes (per-silo storage so different silos can keep their own pin layout).
  const siloKey = silo?.id || "default";
  const siloPins = pins[siloKey] || { left: null, right: null };

  const setSilePin = (side, bumpId) => {
    const next = {
      ...pins,
      [siloKey]: {
        ...siloPins,
        // Only one pin per side. Toggling re-clicks the same bump unpins it.
        [side]: siloPins[side] === bumpId ? null : bumpId,
      },
    };
    setPins(next);
    writePins(next);
  };

  // Build the list of dynamic bumps for both sides.
  const dynamicLeft = useMemo(() => {
    if (!cfg?.left?.[viewport]) return [];
    return (cfg.left[viewport].bumps || []).filter((b) => isDynamic(b.source_type));
  }, [cfg, viewport]);
  const dynamicRight = useMemo(() => {
    if (!cfg?.right?.[viewport]) return [];
    return (cfg.right[viewport].bumps || []).filter((b) => isDynamic(b.source_type));
  }, [cfg, viewport]);

  // Determine which (if any) pinned drawer widths the page should reserve.
  // Write to document.documentElement so any consumer (SiloManage main pane,
  // PublicSilo, etc.) can read var(--mysilo-drawer-left) / right.
  // NOTE: this hook MUST live above any early returns to satisfy React's
  // rules-of-hooks. Cleanup resets the vars when the component unmounts so
  // non-silo pages don't inherit a stale inset.
  const pinnedLeftBump = siloPins.left ? dynamicLeft.find((b) => b.id === siloPins.left) : null;
  const pinnedRightBump = siloPins.right ? dynamicRight.find((b) => b.id === siloPins.right) : null;
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--mysilo-drawer-left", pinnedLeftBump ? `${pinnedLeftBump.width + pinnedLeftBump.drawer_width}px` : "0px");
    root.style.setProperty("--mysilo-drawer-right", pinnedRightBump ? `${pinnedRightBump.width + pinnedRightBump.drawer_width}px` : "0px");
    return () => {
      root.style.setProperty("--mysilo-drawer-left", "0px");
      root.style.setProperty("--mysilo-drawer-right", "0px");
    };
  }, [pinnedLeftBump?.id, pinnedRightBump?.id]);

  // Jul 2026 (Randy) — "Turn off tabs": list the dynamic bumps in the hamburger
  // selector so they stay reachable while their edge handles are hidden.
  useEffect(() => {
    if (!silo || !isOwner) return undefined;
    const tabs = [...dynamicLeft, ...dynamicRight].map((b) => ({ key: `dyn-${b.id}`, label: b.name }));
    if (tabs.length === 0) return undefined;
    return registerHiddenTabs("dynamic-bumps", tabs);
  }, [dynamicLeft, dynamicRight, silo, isOwner]);

  if (!silo || !isOwner) return null;
  if (dynamicLeft.length === 0 && dynamicRight.length === 0) return null;

  const maxLeft = cfg?.left?.[viewport]?.max_slots || 16;
  const maxRight = cfg?.right?.[viewport]?.max_slots || 16;

  return (
    <div data-testid="dynamic-bumps-root">
      {dynamicLeft.map((b) => (
        <DynamicBump key={`L-${b.id}`} bump={b} side="left" maxSlots={maxLeft} silo={silo}
          pinned={siloPins.left === b.id} onTogglePin={() => setSilePin("left", b.id)} />
      ))}
      {dynamicRight.map((b) => (
        <DynamicBump key={`R-${b.id}`} bump={b} side="right" maxSlots={maxRight} silo={silo}
          pinned={siloPins.right === b.id} onTogglePin={() => setSilePin("right", b.id)} />
      ))}
    </div>
  );
}

function DynamicBump({ bump, side, maxSlots, silo, pinned, onTogglePin }) {
  const [open, setOpen] = useState(pinned);
  const [data, setData] = useState(null);
  const [registry, setRegistry] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Pinned drawers are always open. Toggle is a no-op while pinned.
  const drawerOpen = pinned || open;
  // Jul 2026 (Randy) — "Turn off tabs": hide the edge handle; open via hamburger.
  const tabsOff = useTabsOff();
  useOpenTabListener((key) => { if (key === `dyn-${bump.id}` && !pinned) setOpen(true); });

  const surfaceId = SOURCE_TO_SURFACE[bump.source_type] || "workbench_run";
  const Icon = PIcons[bump.icon] || PIcons.Sparkle;

  const loadContent = async () => {
    setRefreshing(true);
    try {
      const r = await api.get(`/silos/${silo.id}/bump-source`, {
        params: { source_type: bump.source_type },
      });
      setData(r.data);
    } catch {
      setData({ content: "", char_count: 0, label: bump.name });
    } finally {
      setRefreshing(false);
    }
  };

  // Lazy-load: only fetch when drawer is open (no point pinging every
  // page with 8 unread slots in the background).
  useEffect(() => {
    if (drawerOpen && !data) loadContent();
  }, [drawerOpen]); // eslint-disable-line

  // Load model registry once for the Staple Module that lives inside.
  useEffect(() => {
    if (!drawerOpen || registry) return;
    api.get("/models/registry").then((r) => setRegistry(r.data)).catch(() => {});
  }, [drawerOpen]); // eslint-disable-line

  const palette = COLOR_SLOT_VAR[bump.color_slot] || COLOR_SLOT_VAR.surface;
  const shapeCls = side === "left"
    ? SHAPE_CLASS[bump.shape] || SHAPE_CLASS["tab-classic"]
    : SHAPE_CLASS_RIGHT[bump.shape] || SHAPE_CLASS_RIGHT["tab-classic"];
  const top = slotToTopPct(bump.slot, maxSlots);
  const sideStyle = side === "left" ? { left: 0 } : { right: 0 };
  const tabClassName = `fixed z-30 flex items-center justify-center text-center font-medium tracking-wide ${shapeCls}`;

  // Build a synthetic "surface" object for the staple module — DynamicBumps
  // pull from iter-30's LLM Routing registry but the registry's surface objects
  // are shaped with default_model/default_provider, which we approximate.
  const stapleSurface = {
    id: surfaceId,
    label: bump.name,
    default_model: surfaceId === "refinery" ? "gemini-2.5-pro" : "gemini-2.5-pro",
    default_provider: "gemini",
  };

  return (
    <>
      {!tabsOff && (
      <button
        type="button"
        onClick={() => { if (!pinned) setOpen((v) => !v); }}
        data-testid={`dynbump-${side}-${bump.id}`}
        className={tabClassName}
        style={{
          ...sideStyle,
          top,
          transform: "translateY(-50%)",
          width: bump.width,
          height: bump.height,
          background: palette.bg,
          color: palette.fg,
          fontSize: bump.text_size,
          writingMode: "vertical-rl",
          textOrientation: "mixed",
          // Subtle outline when pinned so the user can tell at a glance.
          outline: pinned ? `2px solid ${palette.fg}` : "none",
          outlineOffset: pinned ? "-3px" : "0",
        }}
        aria-label={pinned ? `${bump.name} (pinned)` : `Open ${bump.name}`}
      >
        <Icon size={Math.min(16, bump.text_size + 4)} weight={pinned ? "fill" : "duotone"} style={{ writingMode: "horizontal-tb", marginRight: 4 }} />
        {bump.name}
      </button>
      )}
      {drawerOpen && (
        <div
          className="fixed inset-y-0 z-40 bg-white border-l shadow-2xl flex flex-col"
          data-testid={`dynbump-drawer-${side}-${bump.id}`}
          style={{
            ...(side === "left" ? { left: bump.width } : { right: bump.width }),
            width: bump.drawer_width,
            ...(side === "right" ? { borderLeft: "1px solid #E7E5DE", borderRight: "none" } : {}),
          }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b hairline flex items-center gap-2">
            <Icon size={16} weight="duotone" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#1C1B1A]">{data?.label || bump.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">
                {bump.source_type}
              </div>
            </div>
            <button onClick={onTogglePin}
              data-testid={`dynbump-pin-${bump.id}`}
              title={pinned ? "Unpin (collapses to tab)" : "Pin open (stays expanded across reloads)"}
              className={`p-1.5 rounded-md hover:bg-[#F4F2EC] ${pinned ? "text-[#C16E5A]" : "text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
              {pinned ? <PushPinSlash size={14} weight="fill" /> : <PushPin size={14} />}
            </button>
            <button onClick={loadContent} disabled={refreshing}
              data-testid={`dynbump-refresh-${bump.id}`}
              title="Refresh"
              className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#1C1B1A] hover:bg-[#F4F2EC]">
              <ArrowsClockwise size={14} className={refreshing ? "animate-spin" : ""} />
            </button>
            <Link
              to={`/dashboard/silo/${silo.id}?tab=${bump.source_type.startsWith("file-refinery") ? "file-refinery" : "workbench"}`}
              data-testid={`dynbump-jump-${bump.id}`}
              title="Open in main pane"
              onClick={() => { if (!pinned) setOpen(false); }}
              className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F4F2EC]">
              <ArrowSquareOut size={14} />
            </Link>
            {!pinned && (
              <button onClick={() => setOpen(false)} className="text-xs text-[#6B6A68] hover:text-[#1C1B1A] px-2"
                data-testid={`dynbump-close-${bump.id}`}>
                Close
              </button>
            )}
          </div>

          {/* Staple Module — proves the iter-30 portability claim. */}
          {registry && (
            <div className="px-3 pt-3 pb-1 bg-[#FDFCF8]">
              <ModelOutputControl
                surface={stapleSurface}
                scope="silo"
                siloId={silo.id}
                resolved={{ model: stapleSurface.default_model, provider: stapleSurface.default_provider, source: "platform" }}
                registry={registry}
                compact
              />
            </div>
          )}

          {/* Slot content preview */}
          <div className="flex-1 overflow-auto p-4">
            {data === null ? (
              <div className="text-sm text-[#6B6A68]">Loading…</div>
            ) : data.content ? (
              <pre className="whitespace-pre-wrap text-[12px] text-[#1C1B1A] font-mono leading-relaxed"
                data-testid={`dynbump-content-${bump.id}`}>
                {data.content}
              </pre>
            ) : (
              <div className="text-sm text-[#6B6A68] py-8 text-center" data-testid={`dynbump-empty-${bump.id}`}>
                <div className="mb-2">No content yet.</div>
                <Link to={`/dashboard/silo/${silo.id}?tab=${bump.source_type.startsWith("file-refinery") ? "file-refinery" : "workbench"}`}
                  className="text-[#4A5D4E] underline text-xs">
                  Open the {bump.source_type.startsWith("file-refinery") ? "Refinery" : "Workbench"} →
                </Link>
              </div>
            )}
          </div>

          {/* Footer */}
          {data?.char_count > 0 && (
            <div className="px-4 py-2 border-t hairline text-[10px] text-[#6B6A68] tabular-nums">
              {data.char_count.toLocaleString()} chars
              {data.updated_at && <> · updated {new Date(data.updated_at).toLocaleString()}</>}
            </div>
          )}
        </div>
      )}
    </>
  );
}
